// Import core lib dependencies.
import Immutable 		from 'seamless-immutable';

// Import action types.
import ActionTypes  	from './actionTypes.js';

// Config.
import Config			from './config/config.js';
import Enums			from './config/enums.js';

// Iniitial state.
const initialState = Immutable({
});

// Initial state for a component instance.
const initialInstanceState = Immutable({

	state:{
		canParticipate:false,
		hasParticipated:false,
		milestones:null,
		messages:{
			goal:null,
			milestone:null
		}
	},

	progress: {
		value:0,
		percent:0,
		milestones:null,
		stats: {
			lastSampled:null,
			lastValue:null,
			valueIncrease:null,
			rate: {
				current: {
					perSecond:null,
					perMinute:null
				},
				average: {
					perSecond:null,
					perMinute:null
				},
				history:[]
			}
		}
	},

	intervals: {
		progress:null
	},

	showConfirmation:false,
	showError:false,
	errorMessage:null,

	showCaptcha:false
});

// Initial state for a milestone state.
const initialMilestoneStateState = Immutable({
	period:Enums.MILESTONE_PERIOD_NONE,
	reached:false,
	hasParticipated:false
});

// Initial state for a milestone progress.
const initialMilestoneProgressState = Immutable({
	value:0,
	percent:0
});

/**
 * <pre>
 * Goal module Reducer(s).
 *
 * Summary of responsibilities:
 *
 *  - responds to actions, receives current Redux state and modifies it accordingly.
 *
 * FYI:
 *
 * All {@link https://github.com/reactjs/redux|Redux} state modifications are handled using {@link https://github.com/rtfeldman/seamless-immutable|Seamless-immutable} objects and methods.
 * DO NOT DIRECTLY MODIFY THE REDUX STATE (Immutable should stop you from doing this, but just in case)!
 * </pre>
 *
 * @see {@link https://github.com/reactjs/redux|Redux Documentation}
 * @see {@link https://github.com/rtfeldman/seamless-immutable|Seamless-immutable Documentation}
 *
 * @author Tim Loram
 *
 * @memberof module:Goal
 * @namespace
 *
 * @type {Object}
 */
const Reducer = {

	/**
	 * The root reducer for this module. Composes sub-reducers where logical, and passes them the appropriate sub part of the state tree.
	 *
	 * @param  {Object} state The current Redux Immutable state.
	 * @param  {Object} action The dispatched (and received) action.
	 * @return {Object} The modified Immutable Redux state.
	 */
	rootReducer: (state = initialState, action) => {

		switch(action.type) {

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Register a component instance and set its initial state.
			case ActionTypes.REGISTER_INSTANCE:

				// Define the initial state.
				let _initialState = initialInstanceState;

				// Add initial milestone state and progress state.
				let _initialMilestoneState;
				let _initialMilestoneProgress;
				if(action.initialConfig.milestones) {

					_initialMilestoneState 		= {};
					_initialMilestoneProgress 	= {};

					for(let milestoneKey in action.initialConfig.milestones) {

						let milestoneDef = action.initialConfig.milestones[milestoneKey];
						_initialMilestoneState[milestoneDef.id] 	= initialMilestoneStateState;
						_initialMilestoneProgress[milestoneDef.id] 	= initialMilestoneProgressState;
					}

					_initialMilestoneState 		= Immutable(_initialMilestoneState);
					_initialMilestoneProgress 	= Immutable(_initialMilestoneProgress);
				}

				_initialState = _initialState.setIn(['state', 	 'milestones'], _initialMilestoneState);
				_initialState = _initialState.setIn(['progress', 'milestones'], _initialMilestoneProgress);

				// Add initial intervals state.
				_initialState = _initialState.setIn(['intervals'], Immutable(action.initialConfig.intervals));

				// Update state.
				state = state.set(action.id, _initialState);

				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Updated goal state.
			case ActionTypes.UPDATE_STATE:

				state = state.setIn([action.id, 'state'], Immutable(action.state));

				// Return modified state.
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Updated goal progress.
			case ActionTypes.UPDATE_PROGRESS:

				state = state.setIn([action.id, 'progress'], Immutable(action.progress));

				// Return modified state.
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Updated intervals.
			case ActionTypes.UPDATE_INTERVALS:

				state = state.setIn([action.id, 'intervals'], Immutable(action.intervals));

				// Return modified state.
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Show confirmation message.
			case ActionTypes.SHOW_CONFIRMATION:

				state = state.setIn([action.id, 'showConfirmation'], true);
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Hide confirmation message.
			case ActionTypes.HIDE_CONFIRMATION:

				state = state.setIn([action.id, 'showConfirmation'], false);
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Show captcha.
			case ActionTypes.SHOW_CAPTCHA:

				state = state.setIn([action.id, 'showCaptcha'], true);
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Hide captcha.
			case ActionTypes.HIDE_CAPTCHA:

				state = state.setIn([action.id, 'showCaptcha'], false);
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Show error message.
			case ActionTypes.SHOW_ERROR:

				state = state.setIn([action.id, 'showError'], true);
				state = state.setIn([action.id, 'errorMessage'], action.message);
				return state;

			break;

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Hide error message.
			case ActionTypes.HIDE_ERROR:

				state = state.setIn([action.id, 'showError'], false);
				state = state.setIn([action.id, 'errorMessage'], null);
				return state;

			break;

			// Make sure to return a state even if no action was matched!
			default:

				return state;
		}
	}
};

export default Reducer;